import { useState } from 'react';
import { UploadIcon, useToast } from '@/src/components/ui';
import { Input } from '@workspace/ui/components/input';
import { Button } from '@workspace/ui/components/button';
import { Trash2 } from 'lucide-react';
import { IImage } from '@/src/types/IBit';
import { cn } from '@workspace/ui/lib/utils';
import { useProfileUIStore } from '../../profile/store';
import imageCompression from 'browser-image-compression';

export const compressImage = async (file: File): Promise<Blob> => {
  const defaultOptions = {
    maxSizeMB: 5,
    maxWidthOrHeight: 2560,
    initialQuality: 0.9,
    maxIteration: 10,
    useWebWorker: true,
  };

  return await imageCompression(file, defaultOptions);
};

type Props = {
  value: IImage | IImage[] | undefined;
  onChange: (file?: IImage | IImage[]) => void;
  placeholder?: string;
  className?: string;
  isMultiple?: boolean;
  maxSizeBytes?: number;
};

export const ImageUpload = ({
  value,
  onChange,
  placeholder = 'Upload image',
  className = '',
  isMultiple = false,
  maxSizeBytes,
}: Props) => {
  const uploadId = `image-upload-${Math.random().toString(36).substr(2, 9)}`;
  const isMobileView = useProfileUIStore((state) => state.isMobileView);
  const previewMode = useProfileUIStore((state) => state.previewMode);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const toast = useToast();

  const maxSize = maxSizeBytes || 5 * 1024 * 1024;
  const maxSizeLiterally = maxSize / 1024 / 1024 + 'MB';

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (isMultiple && Array.isArray(value)) {
      const cleanedValue = value.filter(
        (item) => item?.file !== undefined && item?.url
      );

      const availableSlots = 4 - cleanedValue.length;
      if (availableSlots <= 0) {
        toast.error(
          `You can upload a maximum of 4 images. You already have ${cleanedValue.length}.`
        );
        return;
      }

      const validFiles = files.filter((file) => {
        if (file.size > maxSize) {
          toast.error(
            `File "${file.name}" is larger than ${maxSizeLiterally} and was skipped`
          );
          return false;
        }
        if (!file.type.startsWith('image/')) {
          toast.error(`File "${file.name}" is not an image and was skipped`);
          return false;
        }
        return true;
      });

      const filesToAdd = validFiles.slice(0, availableSlots);

      if (filesToAdd.length === 0) return;

      const newImages = await Promise.all(
        filesToAdd.map(async (file) => {
          let blob: Blob;
          if (file.type === 'image/gif' || file.type === 'image/webp') {
            blob = file;
          } else {
            blob = await compressImage(file);
          }

          return {
            file: blob,
            url: URL.createObjectURL(blob),
          } as IImage;
        })
      );

      onChange([...cleanedValue, ...newImages]);
      return;
    }

    const file = files[0];
    if (!file) return;

    if (file.size > maxSize) {
      toast.error(`File size must be less than ${maxSizeLiterally}`);
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    const compressedBlob = await compressImage(file);

    onChange({
      file: compressedBlob,
      url: URL.createObjectURL(compressedBlob),
    } as IImage);
  };

  const handleRemove = () => {
    onChange(undefined);
    const input = document.getElementById(uploadId) as HTMLInputElement;
    if (input) input.value = '';
  };

  if (!isMultiple && !Array.isArray(value)) {
    return (
      <div className="space-y-2 flex gap-2 my-4 group">
        {value && value.url && value.url.length > 0 && (
          <div
            className={cn('relative w-full md:w-[270px] h-[190px]', {
              'md:w-[640px] md:h-[290px]': !isMobileView,
            })}
          >
            <img
              src={value.url}
              alt="Uploaded"
              className={cn('object-cover rounded-md size-72', {
                'w-full h-full': !isMobileView || previewMode,
              })}
            />
            {!previewMode && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 bg-white/80 hover:bg-white hidden group-hover:flex"
                onClick={() => handleRemove()}
              >
                <Trash2 className="w-4 h-4 text-red-500 hidden group-hover:flex" />
              </Button>
            )}
          </div>
        )}
        {!value?.url && (
          <div
            className={`p-4 flex items-center justify-center flex-col bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-primary transition-all duration-300 cursor-pointer w-[640px] h-[290px]`}
            onClick={() => document.getElementById(uploadId)?.click()}
          >
            <UploadIcon />
            <Input
              id={uploadId}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />
            <p className="text-sm font-medium text-gray-600">{placeholder}</p>
            <p className="text-xs text-gray-400">
              Max {maxSizeLiterally}
            </p>
          </div>
        )}
      </div>
    );
  }

  const isNotEmptyArray = Array.isArray(value) && value.some((f) => f && f.url);
  const isNotSpace = Array.isArray(value) && value.length === 4;

  const handleRemoveAt = (index: number) => {
    if (Array.isArray(value)) {
      const newArray = [...value];
      newArray.splice(index, 1);
      onChange(newArray);
    }
  };

  return (
    <div className={cn('flex gap-2 flex-wrap', className)}>
      {isNotEmptyArray &&
        value.map((file, idx) => {
          if (!file?.url) return null;
          return (
            <div
              key={idx}
              className="relative group"
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <img
                src={file.url}
                alt={`Uploaded ${idx + 1}`}
                className={cn('object-cover rounded-md size-[155px]', {
                  '': !isMobileView,
                  'size-[120px]': isMobileView,
                })}
              />
              {hoveredIndex === idx && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 bg-white/80 hover:bg-white hidden group-hover:flex"
                  onClick={() => handleRemoveAt(idx)}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          );
        })}
      {!previewMode && !isNotSpace && (
        <div
          className={`p-4 flex items-center justify-center flex-col bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-primary transition-all duration-300 cursor-pointer size-[155px]`}
          onClick={() => document.getElementById(uploadId)?.click()}
        >
          <UploadIcon />
          <Input
            id={uploadId}
            type="file"
            className="hidden"
            accept="image/*"
            multiple
            onChange={handleFileChange}
          />
          <p className="text-sm font-medium text-gray-600">{placeholder}</p>
          <p className="text-xs text-gray-400">Max {maxSizeLiterally}</p>
        </div>
      )}
    </div>
  );
};
